import { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch } from "react-redux";
import {
  IEvent,
  IProgramResume,
  ISpeaker,
  ISpeakerExtended,
} from "../../../interfaces/models/IEvent";
import InputText from "../../../components/Atomos/Inputs/InputText";
import LegacyButton from "../../../components/Atomos/Buttons/LegacyButton";
import WSGCheckbox from "../../../components/Atomos/Checkbox/WSGCheckBox";
import CalendarInput from "../../../components/Atomos/Inputs/CalendarInput";
import DeleteTrashIcon from "../../../assets/images&icons/delete-trash-icon.svg";
import chevronRightButton from "../../../assets/Fonts/chevron-right-button.svg";
import MemberOverviewsTab from "./MemberOverviewsTab";
import { updateEvent, getEventById } from "../../../Services/Event";
import { toastError, toastPromise } from "../../../utils/toast";
import { NewEvent } from "../../../redux/states/event.state";
import "../Events.css";
import "../../../assets/css/Text.css";
import PresentersManager, { PresenterForm } from "./PresentersManager";
import { getCompanyById, getCompanies } from "../../../Services/Company";
import { getGuestSpeakerById } from "../../../Services/GuestSpeaker";
import { getPersonById } from "../../../Services/Person";
import { getFirmById } from "../../../Services/Firm";
import TimeInput from "../../../components/Atomos/Inputs/TimeInput";
import { timestampToTimeString } from "../../../utils/dates";
import { resolvePersonDisplayTitle } from "../../../utils/personDisplayTitle";

const REGIONS = [
  "Global",
  "Africa",
  "Asia",
  "Canada",
  "Caribbean",
  "Central America",
  "Europe",
  "Latin America",
  "Middle East",
  "North America",
  "South America",
  "USA",
];

interface SessionForm {
  id: number;
  title: string;
  date: string;
}

const ensureMinimumSessions = (sessions: SessionForm[]): SessionForm[] => {
  const minSessions = 3;
  if (sessions.length >= minSessions) {
    return sessions;
  }
  const maxId =
    sessions.length > 0 ? Math.max(...sessions.map((s) => s.id)) : -1;
  const additionalSessions = Array.from(
    { length: minSessions - sessions.length },
    (_, i) => ({
      id: maxId + i + 1,
      title: "",
      date: "",
    })
  );
  return [...sessions, ...additionalSessions];
};

const toOptionalTrimmedString = (value: unknown): string | undefined => {
  if (value == null) {
    return undefined;
  }
  const trimmed = String(value).trim();
  return trimmed || undefined;
};

const convertDateStringToTimestamp = (
  dateString: string
): number | undefined => {
  if (!dateString || dateString.trim() === "") {
    return undefined;
  }

  const mmddyyyy = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(dateString);
  if (mmddyyyy) {
    const month = parseInt(mmddyyyy[1], 10) - 1;
    const day = parseInt(mmddyyyy[2], 10);
    const year = parseInt(mmddyyyy[3], 10);
    const date = new Date(Date.UTC(year, month, day, 0, 0, 0));
    if (!isNaN(date.getTime())) {
      return Math.floor(date.getTime() / 1000);
    }
  }

  const isoParts = dateString.split("-");
  if (isoParts.length === 3) {
    const date = new Date(
      parseInt(isoParts[0], 10),
      parseInt(isoParts[1], 10) - 1,
      parseInt(isoParts[2], 10)
    );
    if (!isNaN(date.getTime())) {
      return Math.floor(date.getTime() / 1000);
    }
  }

  const date = new Date(dateString);
  if (!isNaN(date.getTime())) {
    return Math.floor(date.getTime() / 1000);
  }

  return undefined;
};

const isVirtualOrHybridEventType = (eventType?: string): boolean => {
  const normalized = (eventType || "").toLowerCase();
  return normalized === "virtual" || normalized === "hybrid";
};

export default function StaffOverviewTab({
  event,
  isEditMode: externalIsEditMode,
  onEditClick,
  onEventUpdate,
}: {
  event: IEvent | null;
  isEditMode?: boolean;
  onEditClick?: () => void;
  onEventUpdate?: (updatedEvent: IEvent) => void;
}) {
  const dispatch = useDispatch();
  const isEditMode =
    externalIsEditMode !== undefined ? externalIsEditMode : false;
  const [eventType, setEventType] = useState<string>(
    event?.eventType || "hybrid"
  );
  const [overviewTabDescription, setOverviewTabDescription] = useState<string>(
    event?.overviewTabDescription || ""
  );
  const [overviewDescriptionLength, setOverviewDescriptionLength] =
    useState<number>(event?.overviewTabDescription?.length || 0);
  const [attendees, setAttendees] = useState<string>(event?.attendees || "");
  const [attendeesLength, setAttendeesLength] = useState<number>(
    event?.attendees?.length || 0
  );
  const [restrictions, setRestrictions] = useState<string>(
    event?.restrictions || ""
  );
  const [restrictionsLength, setRestrictionsLength] = useState<number>(
    event?.restrictions?.length || 0
  );
  const [programTopic, setProgramTopic] = useState<string>(
    (event as any)?.programTopic || ""
  );
  const [topicDescription, setTopicDescription] = useState<string>(
    (event as any)?.topicDescription || ""
  );
  const [topicDescriptionLength, setTopicDescriptionLength] = useState<number>(
    (event as any)?.topicDescription?.length || 0
  );
  const [presenters, setPresenters] = useState<PresenterForm[]>([]);
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);

  const formatDateToStringUTC = (timestamp: number): string => {
    const date = new Date(timestamp * 1000);
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    const year = date.getUTCFullYear();
    return `${month}/${day}/${year}`;
  };

  const initialSessions =
    event?.overviewProgram && event.overviewProgram.length > 0
      ? ensureMinimumSessions(
          event.overviewProgram.map((prog, idx) => ({
            id: idx,
            title: prog.title,
            date: formatDateToStringUTC(prog.date),
          }))
        )
      : Array.from({ length: 3 }, (_, i) => ({ id: i, title: "", date: "" }));

  const [sessions, setSessions] = useState<SessionForm[]>(initialSessions);
  const [isSaving, setIsSaving] = useState(false);
  const eventIdRef = useRef<string | undefined>();
  const wasEditModeRef = useRef(false);

  const [title, setTitle] = useState<string>(event?.title || "");
  const [eventDescription, setEventDescription] = useState<string>(
    event?.description || ""
  );
  const [location, setLocation] = useState<string>(event?.location || "");
  const [venueLink, setVenueLink] = useState<string>(event?.venueLink || "");
  const [address1, setAddress1] = useState<string>(
    event?.address?.address1 || ""
  );
  const [address2, setAddress2] = useState<string>(
    event?.address?.address2 || ""
  );
  const [city, setCity] = useState<string>(event?.address?.cityName || "");
  const [state, setState] = useState<string>(event?.address?.stateName || "");
  const [zipCode, setZipCode] = useState<string>(event?.address?.zipCode || "");
  const [country, setCountry] = useState<string>(
    event?.address?.countryName || ""
  );
  const [timezone, setTimezone] = useState<string>(event?.timeZone || "");

  const [startDate, setStartDate] = useState<string>(
    event?.startDate ? formatDateToStringUTC(event.startDate) : ""
  );
  const [startTime, setStartTime] = useState<string>(
    event?.startDate ? timestampToTimeString(event.startDate) : ""
  );
  const [endDate, setEndDate] = useState<string>(
    event?.endDate ? formatDateToStringUTC(event.endDate) : ""
  );
  const [endTime, setEndTime] = useState<string>(
    event?.endDate ? timestampToTimeString(event.endDate) : ""
  );

  const syncFormFromEvent = useCallback((sourceEvent: IEvent) => {
    setEventType(sourceEvent.eventType || "hybrid");
    setTitle(sourceEvent.title || "");
    setEventDescription(sourceEvent.description || "");
    setLocation(sourceEvent.location || "");
    setVenueLink(sourceEvent.venueLink || "");
    setAddress1(sourceEvent.address?.address1 || "");
    setAddress2(sourceEvent.address?.address2 || "");
    setCity(sourceEvent.address?.cityName || "");
    setState(sourceEvent.address?.stateName || "");
    setZipCode(sourceEvent.address?.zipCode || "");
    setCountry(sourceEvent.address?.countryName || "");
    setTimezone(sourceEvent.timeZone || "");

    setStartDate(
      sourceEvent?.startDate ? formatDateToStringUTC(sourceEvent.startDate) : ""
    );
    setStartTime(
      sourceEvent?.startDate ? timestampToTimeString(sourceEvent.startDate) : ""
    );
    setEndDate(
      sourceEvent?.endDate ? formatDateToStringUTC(sourceEvent.endDate) : ""
    );
    setEndTime(
      sourceEvent?.endDate ? timestampToTimeString(sourceEvent.endDate) : ""
    );

    setOverviewTabDescription(sourceEvent.overviewTabDescription || "");
    setOverviewDescriptionLength(
      sourceEvent.overviewTabDescription?.length || 0
    );
    setAttendees(toOptionalTrimmedString(sourceEvent.attendees) || "");
    setAttendeesLength(
      toOptionalTrimmedString(sourceEvent.attendees)?.length || 0
    );
    setRestrictions(sourceEvent.restrictions || "");
    setRestrictionsLength(sourceEvent.restrictions?.length || 0);

    setProgramTopic((sourceEvent as any)?.programTopic || "");
    setTopicDescription((sourceEvent as any)?.topicDescription || "");
    setTopicDescriptionLength((sourceEvent as any)?.topicDescription?.length || 0);

    const speakersFromEvent: ISpeakerExtended[] =
      (sourceEvent as any)?.speakers && Array.isArray((sourceEvent as any).speakers)
        ? (sourceEvent as any).speakers
        : [];

    const formattedPresenters = speakersFromEvent.map((speaker) => ({
      personType: speaker.speakerType,
      memberFirm: speaker.firmName
        ? { id: "", name: speaker.firmName }
        : null,
      professional: speaker.name
        ? { id: speaker.personId, name: speaker.name }
        : null,
    }));
    setPresenters(formattedPresenters);

    if (sourceEvent.overviewProgram && sourceEvent.overviewProgram.length > 0) {
      const syncedSessions = sourceEvent.overviewProgram.map((prog, idx) => ({
        id: idx,
        title: prog.title,
        date: formatDateToStringUTC(prog.date),
      }));
      setSessions(ensureMinimumSessions(syncedSessions));
    } else {
      setSessions(
        Array.from({ length: 3 }, (_, i) => ({ id: i, title: "", date: "" }))
      );
    }
  }, []);

  useEffect(() => {
    if (!event) {
      return;
    }

    const eventIdChanged = event.id !== eventIdRef.current;
    const enteredEditMode = isEditMode && !wasEditModeRef.current;

    if (eventIdChanged || enteredEditMode) {
      syncFormFromEvent(event);
      eventIdRef.current = event.id;
    }

    wasEditModeRef.current = isEditMode;
  }, [event, isEditMode, syncFormFromEvent]);

  if (!event) {
    return <div>Event not found</div>;
  }

  const handleRegionChange = (region: string) => {
    setSelectedRegions((prev) =>
      prev.includes(region)
        ? prev.filter((r) => r !== region)
        : [...prev, region]
    );
  };

  const handleRemoveSession = (sessionId: number) => {
    const session = sessions.find((s) => s.id === sessionId);
    if (!session) {
      return;
    }

    const hasData =
      (session.title && session.title.trim() !== "") ||
      (session.date && session.date.trim() !== "");

    if (hasData) {
      const clearedSessions = sessions.map((s) =>
        s.id === sessionId ? { ...s, title: "", date: "" } : s
      );

      const sessionsWithIndex = clearedSessions.map((s, index) => ({
        session: s,
        originalIndex: index,
      }));
      const sortedSessions = sessionsWithIndex.sort((a, b) => {
        const aHasData =
          (a.session.title && a.session.title.trim() !== "") ||
          (a.session.date && a.session.date.trim() !== "");
        const bHasData =
          (b.session.title && b.session.title.trim() !== "") ||
          (b.session.date && b.session.date.trim() !== "");

        if (aHasData && !bHasData) {
          return -1;
        }
        if (!aHasData && bHasData) {
          return 1;
        }
        return a.originalIndex - b.originalIndex;
      });
      const finalSessions = sortedSessions.map((item) => item.session);
      setSessions(ensureMinimumSessions(finalSessions));
    }
  };

  const handleSessionTitleChange = (sessionId: number, value: string) => {
    setSessions(
      sessions.map((s) => (s.id === sessionId ? { ...s, title: value } : s))
    );
  };

  const handleSessionDateChange = (sessionId: number, value: string) => {
    setSessions(
      sessions.map((s) => (s.id === sessionId ? { ...s, date: value } : s))
    );
  };

  // Helper functions to handle string | undefined from InputText onChange
  const handleTitleChange = (value: string | undefined) =>
    setTitle(value || "");
  const handleEventDescriptionChange = (value: string | undefined) =>
    setEventDescription(value || "");
  const handleLocationChange = (value: string | undefined) =>
    setLocation(value || "");
  const handleVenueLinkChange = (value: string | undefined) =>
    setVenueLink(value || "");
  const handleAddress1Change = (value: string | undefined) =>
    setAddress1(value || "");
  const handleAddress2Change = (value: string | undefined) =>
    setAddress2(value || "");
  const handleCityChange = (value: string | undefined) => setCity(value || "");
  const handleStateChange = (value: string | undefined) =>
    setState(value || "");
  const handleZipCodeChange = (value: string | undefined) =>
    setZipCode(value || "");
  const handleCountryChange = (value: string | undefined) =>
    setCountry(value || "");
  const handleTimezoneChange = (value: string | undefined) =>
    setTimezone(value || "");

  const handleSubmit = async () => {
    if (!event || !event.id || isSaving) {
      if (!event?.id) {
        toastError("Event ID is required to save changes");
      }
      return;
    }

    setIsSaving(true);

    try {
      const effectiveEventType = event.eventType || eventType;
      const includeVirtualFields = isVirtualOrHybridEventType(effectiveEventType);

      const overviewProgram: IProgramResume[] = sessions
        .filter(
          (session) => session.title.trim() !== "" && session.date.trim() !== ""
        )
        .map((session) => {
          const timestamp = convertDateStringToTimestamp(session.date);
          return {
            title: session.title.trim(),
            date: timestamp ?? 0,
          };
        })
        .filter((session) => session.date > 0);

      let speakers: ISpeaker[] = [];
      if (includeVirtualFields) {
        const existingSpeakers =
          (event as any)?.speakers && Array.isArray((event as any).speakers)
            ? ((event as any).speakers as ISpeaker[])
            : [];

        const speakersPromises = presenters
          .filter((p) => p.professional)
          .map(async (p) => {
            const existingSpeaker = existingSpeakers.find(
              (s) =>
                s.personId === p.professional!.id &&
                s.speakerType === p.personType
            );

            const baseSpeaker: ISpeakerExtended = existingSpeaker
              ? { ...existingSpeaker }
              : {
                  personId: p.professional!.id,
                  speakerType: p.personType,
                  name: p.professional!.name,
                };

            if (p.personType === "guest") {
              try {
                let guestSpeaker = null;
                if (!baseSpeaker.photo || !baseSpeaker.title) {
                  guestSpeaker = await getGuestSpeakerById(p.professional!.id);

                  if (guestSpeaker) {
                    baseSpeaker.photo =
                      baseSpeaker.photo || guestSpeaker.photo || undefined;
                    baseSpeaker.title =
                      baseSpeaker.title ||
                      resolvePersonDisplayTitle(guestSpeaker) ||
                      undefined;
                  }
                }

                if (!baseSpeaker.firmLogo || !baseSpeaker.firmName) {
                  if (p.memberFirm?.id && p.memberFirm.id.trim() !== "") {
                    try {
                      const company = await getCompanyById(p.memberFirm.id);
                      if (company) {
                        baseSpeaker.firmName =
                          baseSpeaker.firmName ||
                          company.name ||
                          p.memberFirm.name ||
                          undefined;
                        baseSpeaker.firmLogo =
                          baseSpeaker.firmLogo || company.logo || undefined;
                      }
                    } catch (companyError) {
                      console.error(
                        "Error fetching company by ID:",
                        companyError
                      );
                    }
                  }

                  if (
                    !baseSpeaker.firmLogo &&
                    (baseSpeaker.firmName || p.memberFirm?.name)
                  ) {
                    const searchName =
                      baseSpeaker.firmName ||
                      p.memberFirm?.name ||
                      guestSpeaker?.companyName;
                    if (searchName) {
                      try {
                        const allCompanies = await getCompanies();
                        const matchingCompany = allCompanies.find(
                          (c) =>
                            c.name?.toLowerCase() === searchName.toLowerCase()
                        );
                        if (matchingCompany) {
                          baseSpeaker.firmName =
                            baseSpeaker.firmName ||
                            matchingCompany.name ||
                            undefined;
                          baseSpeaker.firmLogo =
                            baseSpeaker.firmLogo ||
                            matchingCompany.logo ||
                            undefined;
                        }
                      } catch (companiesError) {
                        console.error(
                          "Error fetching companies:",
                          companiesError
                        );
                      }
                    }
                  }

                  if (!baseSpeaker.firmName) {
                    baseSpeaker.firmName =
                      guestSpeaker?.companyName ||
                      p.memberFirm?.name ||
                      undefined;
                  }
                }
              } catch (error) {
                console.error("Error fetching guest speaker data:", error);
              }
            } else {
              if (
                !baseSpeaker.photo ||
                !baseSpeaker.title ||
                !baseSpeaker.firmName
              ) {
                try {
                  const person = await getPersonById(p.professional!.id);

                  if (person) {
                    baseSpeaker.photo =
                      baseSpeaker.photo || person.photo || undefined;
                    baseSpeaker.title =
                      baseSpeaker.title ||
                      resolvePersonDisplayTitle(person) ||
                      undefined;

                    if (
                      (!baseSpeaker.firmName || !baseSpeaker.firmLogo) &&
                      person.firm
                    ) {
                      try {
                        const firm = await getFirmById(person.firm);
                        baseSpeaker.firmName =
                          baseSpeaker.firmName || firm?.name || undefined;
                        baseSpeaker.firmLogo =
                          baseSpeaker.firmLogo || firm?.logo || undefined;
                      } catch (firmError) {
                        console.error("Error fetching firm data:", firmError);
                      }
                    }
                  }
                } catch (error) {
                  console.error("Error fetching speaker/person data:", error);
                }
              }
            }

            return baseSpeaker;
          });

        speakers = await Promise.all(speakersPromises);
      }

      const {
        contactUsDetails: _contactUsDetails,
        specialEventTemplate: _specialEventTemplate,
        isPastEvent: _isPastEvent,
        ...eventWithoutComputed
      } = event as IEvent & {
        contactUsDetails?: unknown;
        specialEventTemplate?: unknown;
        isPastEvent?: boolean;
      };

      const updatedEvent: IEvent = {
        ...eventWithoutComputed,
        overviewTabDescription: toOptionalTrimmedString(overviewTabDescription),
        attendees: toOptionalTrimmedString(attendees),
        restrictions: toOptionalTrimmedString(restrictions),
        overviewProgram:
          overviewProgram.length > 0 ? overviewProgram : undefined,
        ...(includeVirtualFields
          ? {
              programTopic: toOptionalTrimmedString(programTopic) as any,
              topicDescription: toOptionalTrimmedString(topicDescription) as any,
              speakers,
            }
          : {}),
      };

      const promise = updateEvent(updatedEvent);
      await toastPromise(promise, {
        pending: "Saving overview data...",
        success: "Overview data saved successfully!",
        error: "Failed to save overview data",
      });

      const reloadedEvent = await getEventById(event.id);

      if (reloadedEvent) {
        dispatch(NewEvent(reloadedEvent));

        if (onEventUpdate) {
          onEventUpdate(reloadedEvent);
        }
      }

      if (onEditClick) {
        onEditClick();
      }
    } catch (error) {
      toastError(error as string);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isEditMode) {
    return <MemberOverviewsTab />;
  }

  return (
    <div className="flex flex-col items-start self-stretch">
      <div className="space-y-[10px] w-full">
        <h2 className="text-Default font-decimal text-[32px] font-medium leading-[36px] text-left">
          Description
        </h2>
        <div className="space-y-4 w-full">
          <label className="text-lg font-semibold text-gray-font leading-normal text-left block">
            Event Type
          </label>
          <div className="flex gap-6 w-full">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="eventType"
                value="inPerson"
                checked={eventType === "inPerson"}
                onChange={(e) => setEventType(e.target.value)}
                disabled={true}
                className="w-4 h-4 text-accent-Default focus:ring-accent-Default"
              />
              <span className="text-base text-gray-font">In-Person</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="eventType"
                value="virtual"
                checked={eventType === "virtual"}
                onChange={(e) => setEventType(e.target.value)}
                disabled={true}
                className="w-4 h-4 text-accent-Default focus:ring-accent-Default"
              />
              <span className="text-base text-gray-font">Virtual</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="eventType"
                value="hybrid"
                checked={eventType === "hybrid"}
                onChange={(e) => setEventType(e.target.value)}
                disabled={true}
                className="w-4 h-4 text-accent-Default focus:ring-accent-Default"
              />
              <span className="text-base text-gray-font">Hybrid</span>
            </label>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
          <InputText
            title="Title of the Event"
            required={false}
            name="title"
            disabled={true}
            value={title}
            onChange={handleTitleChange}
            labelClassName="text-lg font-semibold text-gray-font leading-normal"
          />

          <InputText
            title="Event Description"
            required={false}
            name="eventDescription"
            disabled={true}
            value={eventDescription}
            onChange={handleEventDescriptionChange}
            labelClassName="text-lg font-semibold text-gray-font leading-normal"
          />
        </div>

        <div className="flex flex-col gap-2.5 self-stretch items-start">
          <label className="text-lg font-semibold text-gray-font leading-normal text-left">
            Overview Tab Description*
          </label>
          <textarea
            value={overviewTabDescription}
            rows={6}
            maxLength={3000}
            placeholder="Enter a brief description of your professional background."
            onChange={(e) => {
              const value = e.target.value;
              setOverviewTabDescription(value);
              setOverviewDescriptionLength(value.length);
            }}
            className="w-full px-3 py-2 bg-[#F4F4F4] text-gray-900 min-h-[120px] focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y border border-[#F4F4F4]"
          />
          <div className="flex justify-end w-full mt-1">
            <span className="text-xs text-gray-500">
              {overviewDescriptionLength} / 3000
            </span>
          </div>
        </div>
      </div>

      <div className="w-full h-px bg-[#F4F4F4] my-[48px]"></div>
      <div className="space-y-[10px] w-full">
        <h2 className="text-Default font-decimal text-[32px] font-medium leading-[36px] text-left">
          Location
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
          <InputText
            title="Location (Building name, Hotel, Convention Center)"
            required={false}
            name="location"
            disabled={true}
            value={location}
            onChange={handleLocationChange}
            labelClassName="text-lg font-semibold text-gray-font leading-normal"
          />

          <InputText
            title="Venue Link"
            required={false}
            name="venueLink"
            disabled={true}
            value={venueLink}
            onChange={handleVenueLinkChange}
            labelClassName="text-lg font-semibold text-gray-font leading-normal"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
          <InputText
            title="Address 1 (Street, Number)"
            required={false}
            name="address1"
            disabled={true}
            value={address1}
            onChange={handleAddress1Change}
            labelClassName="text-lg font-semibold text-gray-font leading-normal"
          />
          <InputText
            title="Address 2 (Street, Number)"
            required={false}
            name="address2"
            disabled={true}
            value={address2}
            onChange={handleAddress2Change}
            labelClassName="text-lg font-semibold text-gray-font leading-normal"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 w-full">
          <InputText
            title="City"
            required={false}
            name="city"
            disabled={true}
            value={city}
            onChange={handleCityChange}
            labelClassName="text-lg font-semibold text-gray-font leading-normal"
          />
          <InputText
            title="State / Province"
            required={false}
            name="state"
            disabled={true}
            value={state}
            onChange={handleStateChange}
            labelClassName="text-lg font-semibold text-gray-font leading-normal"
          />
          <InputText
            title="Zip Code"
            required={false}
            name="zipCode"
            disabled={true}
            value={zipCode}
            onChange={handleZipCodeChange}
            labelClassName="text-lg font-semibold text-gray-font leading-normal"
          />
          <InputText
            title="Country"
            required={false}
            name="country"
            disabled={true}
            value={country}
            onChange={handleCountryChange}
            labelClassName="text-lg font-semibold text-gray-font leading-normal"
          />
        </div>

        <div className="space-y-4 w-full">
          <label className="text-lg font-semibold text-gray-font leading-normal text-left">
            Region
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 w-full">
            {REGIONS.map((region, index) => (
              <WSGCheckbox
                key={region}
                index={index}
                text={region}
                checked={selectedRegions.includes(region)}
                handleCheckboxChange={() => handleRegionChange(region)}
                disable={true}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="w-full h-px bg-[#F4F4F4] my-[48px]"></div>
      <div className="space-y-[10px] w-full">
        <h2 className="text-Default font-decimal text-[32px] font-medium leading-[36px] text-left">
          Dates
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 w-full">
          <div className="space-y-4 w-full">
            <div className="grid grid-cols-2 gap-4">
              <CalendarInput
                title="Start Date"
                type="single"
                error={false}
                value={startDate}
                onChange={(e) => setStartDate(e)}
                required={false}
                disabled={true}
              />
              <TimeInput
                title="Time"
                value={startTime}
                placeholder="HH:MM AM/PM"
                onChange={e => setStartTime(e)}
                minuteInterval={15}
                disabled
              />
            </div>
          </div>

          <div className="space-y-4 w-full">
            <div className="grid grid-cols-2 gap-4">
              <CalendarInput
                title="End Date"
                type="single"
                error={false}
                value={endDate}
                onChange={(e) => setEndDate(e)}
                required={false}
                disabled={true}
              />
              <TimeInput
                title="Time"
                value={endTime}
                placeholder="HH:MM AM/PM"
                onChange={e => setEndTime(e)}
                minuteInterval={15}
                disabled
              />
            </div>
          </div>

          <div className="space-y-4 w-full">
            <InputText
              title="Timezone"
              required={false}
              name="timezone"
              disabled={true}
              value={timezone}
              onChange={handleTimezoneChange}
              labelClassName="text-lg font-semibold text-gray-font leading-normal"
            />
          </div>
        </div>
      </div>

      {(eventType === "virtual" || eventType === "hybrid") && event?.setupType !== "luncheon" && (
        <>
          <div className="w-full h-px bg-[#F4F4F4] my-[48px]"></div>
          <div className="space-y-[10px] w-full">
            <h2 className="text-Default font-decimal text-[32px] font-medium leading-[36px] text-left">
              Program Topic
            </h2>

            <div className="flex flex-col gap-2.5 self-stretch items-start">
              <label className="text-lg font-semibold text-gray-font leading-normal text-left">
                Program Topic
              </label>
              <input
                type="text"
                value={programTopic}
                onChange={(e) => setProgramTopic(e.target.value)}
                className="w-full px-3 py-2 bg-[#F4F4F4] text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 border border-[#F4F4F4]"
                placeholder="Enter program topic"
              />
            </div>
          </div>

          <div className="w-full h-px bg-[#F4F4F4] my-[48px]"></div>
          <div className="space-y-[10px] w-full">
            <h2 className="text-Default font-decimal text-[32px] font-medium leading-[36px] text-left">
              Topic Description
            </h2>

            <div className="flex flex-col gap-2.5 self-stretch items-start">
              <label className="text-lg font-semibold text-gray-font leading-normal text-left">
                Topic Description
              </label>
              <textarea
                value={topicDescription}
                rows={6}
                maxLength={3000}
                placeholder="Enter topic description"
                onChange={(e) => {
                  const value = e.target.value;
                  setTopicDescription(value);
                  setTopicDescriptionLength(value.length);
                }}
                className="w-full px-3 py-2 bg-[#F4F4F4] text-gray-900 min-h-[120px] focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y border border-[#F4F4F4]"
              />
              <div className="flex justify-end w-full mt-1">
                <span className="text-xs text-gray-500">
                  {topicDescriptionLength} / 3000
                </span>
              </div>
            </div>
          </div>

          <div className="w-full h-px bg-[#F4F4F4] my-[48px]"></div>
          <div className="space-y-[10px] w-full">
            <PresentersManager
              presenters={presenters}
              onPresentersChange={setPresenters}
              title="Speakers"
              route="EventSpeakers"
            />
          </div>
        </>
      )}

      {(eventType.toLowerCase() === "hybrid" ||
        eventType.toLowerCase() === "inperson") && event?.setupType !== "luncheon" && (
        <div className="w-full h-px bg-[#F4F4F4] my-[48px]"></div>
      )}
      {(eventType.toLowerCase() === "hybrid" ||
        eventType.toLowerCase() === "inperson") && event?.setupType !== "luncheon" && (
        <div className="space-y-[10px] w-full">
          <h2 className="text-Default font-decimal text-[32px] font-medium leading-[36px] text-left">
            Program Agenda
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 w-full">
            {sessions.map((session, index) => {
              const isLastColumn = (index + 1) % 3 === 0;
              const isFirstRow = index < 3;
              const isLastRow =
                index >= Math.floor((sessions.length - 1) / 3) * 3;
              return (
                <div
                  key={session.id}
                  className={`space-y-4 p-4 ${
                    !isLastColumn ? "border-r border-[#F4F4F4]" : ""
                  } ${
                    !isFirstRow && !isLastRow ? "border-t border-[#F4F4F4]" : ""
                  } ${!isLastRow ? "border-b border-[#F4F4F4]" : ""}`}
                >
                  <InputText
                    title="Title of Session"
                    required={false}
                    name={`session-title-${session.id}`}
                    value={session.title}
                    onChange={(value) =>
                      handleSessionTitleChange(session.id, value || "")
                    }
                    labelClassName="text-lg font-semibold text-gray-font leading-normal"
                  />
                  <div className="space-y-2">
                    <label className="text-lg font-semibold text-gray-font leading-normal text-left">
                      Date of Session
                    </label>
                    <CalendarInput
                      key={`session-date-${session.id}-${
                        session.date || "empty"
                      }`}
                      title=""
                      type="single"
                      error={false}
                      value={session.date || ""}
                      defaultValue={session.date || ""}
                      onChange={(e) => handleSessionDateChange(session.id, e)}
                      required={false}
                    />
                  </div>
                  <div className="flex justify-start">
                    <LegacyButton
                      text="REMOVE SESSION"
                      icon={
                        <img
                          src={DeleteTrashIcon}
                          alt="Delete"
                          className="w-5 h-5"
                        />
                      }
                      color="delete"
                      style="outlined"
                      size="small"
                      content="textIcon"
                      iconOrder="textFirst"
                      onClick={() => handleRemoveSession(session.id)}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="w-full h-px bg-[#F4F4F4] my-[48px]"></div>
      <div className="space-y-[10px] w-full">
        <h2 className="text-Default font-decimal text-[32px] font-medium leading-[36px] text-left">
          Attendees
        </h2>
        <div className="flex flex-col gap-2.5 self-stretch items-start">
          <label className="text-lg font-semibold text-gray-font leading-normal text-left">
            Attendees
          </label>
          <textarea
            value={attendees}
            rows={6}
            maxLength={3000}
            onChange={(e) => {
              const value = e.target.value;
              setAttendees(value);
              setAttendeesLength(value.length);
            }}
            className="w-full px-3 py-2 bg-[#F4F4F4] text-gray-900 min-h-[120px] focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y border border-[#F4F4F4]"
          />
          <div className="flex justify-end w-full mt-1">
            <span className="text-xs text-gray-500">
              {attendeesLength} / 3000
            </span>
          </div>
        </div>
      </div>

      <div className="w-full h-px bg-[#F4F4F4] my-[48px]"></div>
      <div className="space-y-[10px] w-full">
        <h2 className="text-Default font-decimal text-[32px] font-medium leading-[36px] text-left">
          Restrictions
        </h2>
        <div className="flex flex-col gap-2.5 self-stretch items-start">
          <label className="text-lg font-semibold text-gray-font leading-normal text-left">
            Restrictions
          </label>
          <textarea
            value={restrictions}
            rows={6}
            maxLength={3000}
            onChange={(e) => {
              const value = e.target.value;
              setRestrictions(value);
              setRestrictionsLength(value.length);
            }}
            className="w-full px-3 py-2 bg-[#F4F4F4] text-gray-900 min-h-[120px] focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y border border-[#F4F4F4]"
          />
          <div className="flex justify-end w-full mt-1">
            <span className="text-xs text-gray-500">
              {restrictionsLength} / 3000
            </span>
          </div>
        </div>
      </div>
      <div className="flex justify-end w-full mt-[48px]">
        <LegacyButton
          text={isSaving ? "SUBMITTING..." : "SUBMIT"}
          icon={
            <img src={chevronRightButton} alt="Submit" className="w-3 h-3" />
          }
          color="accent"
          style="filled"
          size="small"
          content="textIcon"
          iconOrder="textFirst"
          onClick={handleSubmit}
          disabled={isSaving}
        />
      </div>
    </div>
  );
}
